import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { getMachineTimeseries } from "../controllers/machineController";

const pad2 = (n) => String(n).padStart(2, "0");
const formatClock = (ms) => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const POLL_MS = 15000; // same cadence as /latest & /status
const PANEL_W = 560;
const PANEL_H = 470; // used only for viewport clamping

// Logical metric -> aggregate key + per-phase key resolver.
// Frequency and TAP have no per-phase field, so they are dropped when a phase is picked.
const METRICS = [
  { id: "power", label: "Power", unit: "kW", agg: "power_kw", phase: (n) => `p${n}`, isDefault: true, color: "#1BA08E" },
  { id: "voltage", label: "Voltage", unit: "V", agg: "voltage", phase: (n) => `v${n}`, isDefault: false, color: "#3b82f6" },
  { id: "current", label: "Current", unit: "A", agg: "current", phase: (n) => `i${n}`, isDefault: false, color: "#f59e0b" },
  { id: "pf", label: "Power Factor", unit: "", agg: "power_factor", phase: (n) => `pf${n}`, isDefault: false, color: "#8b5cf6" },
  { id: "freq", label: "Frequency", unit: "Hz", agg: "frequency", phase: null, isDefault: false, color: "#ec4899" },
  { id: "thdv", label: "THD Voltage", unit: "%", agg: "thd_voltage", phase: (n) => `thdv${n}`, isDefault: false, color: "#0ea5e9" },
  { id: "thdi", label: "THD Current", unit: "%", agg: "thd_current", phase: (n) => `thdi${n}`, isDefault: false, color: "#ef4444" },
  { id: "tap", label: "TAP", unit: "", agg: "tap", phase: null, isDefault: false, color: "#64748b" },
];

export default function MachineTimeSeriesPanel({ node, pos, username, viewMode = "linear", onViewModeChange, onClose }) {
  const machineName = node?.name;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("All"); // "All" | "1" | "2" | "3"
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(METRICS.filter((m) => m.isDefault).map((m) => m.id)),
  );
  const mountedRef = useRef(true);

  const toggleMetric = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const fetchData = useCallback(
    async (isPoll) => {
      if (!username || !machineName) return;
      if (!isPoll) setLoading(true);
      try {
        const res = await getMachineTimeseries(username, machineName);
        if (!mountedRef.current) return;
        if (res && res.status === "success") {
          setData(Array.isArray(res.data) ? res.data : []);
          setError("");
        } else {
          setError(res?.message || "Failed to load time-series.");
        }
      } catch (e) {
        if (mountedRef.current) {
          setError(e?.response?.data?.message || e.message || "Failed to load time-series.");
        }
      } finally {
        if (mountedRef.current && !isPoll) setLoading(false);
      }
    },
    [username, machineName],
  );

  // Initial fetch + live poll (re-fetch the 10-min window every 5s and replace).
  useEffect(() => {
    mountedRef.current = true;
    fetchData(false);
    const iv = setInterval(() => fetchData(true), POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(iv);
    };
  }, [fetchData]);

  const phaseNum = phase === "All" ? null : phase;

  // Phase switch / All toggle only re-render — no re-fetch (response has both).
  const visibleMetrics = useMemo(
    () =>
      METRICS.filter((m) => {
        if (!selectedIds.has(m.id)) return false; // user picked it via chips
        if (phaseNum && !m.phase) return false; // freq/tap: no per-phase data
        return true;
      }),
    [selectedIds, phaseNum],
  );

  const getTimestampMs = (d) => {
    if (d.timestamp_ms && Number.isFinite(d.timestamp_ms)) return d.timestamp_ms;
    if (d.timestamp) {
      const t = new Date(d.timestamp).getTime();
      if (Number.isFinite(t)) return t;
    }
    return null;
  };

  const series = useMemo(
    () =>
      visibleMetrics.map((m) => {
        const key = phaseNum ? m.phase(phaseNum) : m.agg;
        return {
          name: m.unit ? `${m.label} (${m.unit})` : m.label,
          data: data
            .map((d) => {
              const raw = d[key];
              const n = parseFloat(raw);
              const val = Number.isFinite(n) ? n : null;
              const ms = getTimestampMs(d);
              return [ms, val];
            })
            .filter(([ms]) => ms !== null),
        };
      }),
    [visibleMetrics, data, phaseNum],
  );

  const xRange = useMemo(() => {
    if (!data.length) return { min: undefined, max: undefined };
    const validTimestamps = data.map(getTimestampMs).filter((t) => t !== null);
    if (!validTimestamps.length) return { min: undefined, max: undefined };
    const latestMs = Math.max(...validTimestamps);
    const firstMs = Math.min(...validTimestamps);
    return { min: Math.min(firstMs, latestMs - 10 * 60 * 1000), max: latestMs };
  }, [data]);

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: visibleMetrics.map((m) => m.color),
      stroke: { width: 2, curve: "smooth" },
      dataLabels: { enabled: false },
      legend: { position: "top", horizontalAlign: "left", fontSize: "11px" },
      grid: { borderColor: "#f1f5f9" },
      xaxis: {
        type: "datetime",
        min: xRange.min,
        max: xRange.max,
        labels: {
          style: { colors: "#94a3b8", fontSize: "10px" },
          datetimeUTC: false,
          format: "HH:mm",
        },
      },
      yaxis: visibleMetrics.map((m) => {
        const single = visibleMetrics.length === 1;
        return {
          seriesName: m.unit ? `${m.label} (${m.unit})` : m.label,
          show: single,
          labels: {
            style: { colors: m.color, fontSize: "10px" },
            formatter: (v) => (v == null ? "" : Number(v).toFixed(m.id === "pf" ? 2 : 1)),
          },
          title: single
            ? {
              text: m.unit ? `${m.label} (${m.unit})` : m.label,
              style: { color: m.color, fontSize: "10px", fontWeight: 600 },
            }
            : undefined,
        };
      }),
      tooltip: {
        shared: true,
        x: { formatter: (val) => formatClock(val) },
        y: { formatter: (v) => (v == null ? "-" : Number(v).toFixed(2)) },
      },
      noData: { text: "No data in the last 10 minutes" },
    }),
    [visibleMetrics, series, xRange],
  );

  // Clamp the floating panel inside the viewport (it's wider than the tabular panel).
  const left = Math.max(12, Math.min(pos?.left ?? 40, window.innerWidth - PANEL_W - 12));
  const top = Math.max(12, Math.min(pos?.top ?? 40, window.innerHeight - PANEL_H - 12));

  return (
    <div
      style={{ ...panelStyle, top, left, width: PANEL_W }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={headerStyle}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{machineName}</span>
        <button onClick={onClose} style={closeBtnStyle} aria-label="Close">
          ×
        </button>
      </div>

      {/* Toggle between Tabular View and Linear View */}
      <div style={{ padding: "10px 14px 0" }}>
        <div style={{
          display: "flex",
          background: "#f1f5f9",
          borderRadius: 6,
          padding: 2,
          gap: 2,
        }}>
          <button
            type="button"
            onClick={() => onViewModeChange && onViewModeChange("tabular")}
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 12,
              fontWeight: 500,
              color: "#64748b",
              background: "transparent",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Tabular View
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange && onViewModeChange("linear")}
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 12,
              fontWeight: 700,
              color: "#0f172a",
              background: "#ffffff",
              border: "none",
              borderRadius: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              cursor: "pointer",
            }}
          >
            Linear View
          </button>
        </div>
      </div>

      <div style={controlsRowStyle}>
        <label style={controlLabelStyle}>Phase</label>
        <select value={phase} onChange={(e) => setPhase(e.target.value)} style={selectStyle}>
          <option value="All">All</option>
          <option value="1">Phase 1</option>
          <option value="2">Phase 2</option>
          <option value="3">Phase 3</option>
        </select>
        <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>
          Tap a parameter to show / hide
        </span>
      </div>

      <div style={chipsRowStyle}>
        {METRICS.map((m) => {
          const disabled = Boolean(phaseNum) && !m.phase; // no per-phase data
          const active = selectedIds.has(m.id) && !disabled;
          return (
            <button
              key={m.id}
              onClick={() => !disabled && toggleMetric(m.id)}
              disabled={disabled}
              title={disabled ? "No per-phase data for this parameter" : ""}
              style={{
                ...chipStyle,
                ...(active ? { background: m.color, borderColor: m.color, color: "#fff" } : {}),
                ...(disabled ? chipDisabledStyle : {}),
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: "6px 10px 10px" }}>
        {loading ? (
          <div style={centerStyle}>Loading…</div>
        ) : error ? (
          <div style={{ ...centerStyle, color: "#dc2626" }}>{error}</div>
        ) : visibleMetrics.length === 0 ? (
          <div style={centerStyle}>Select at least one parameter above</div>
        ) : (
          <ReactApexChart options={options} series={series} type="line" height={300} />
        )}
      </div>
    </div>
  );
}

const panelStyle = {
  position: "fixed",
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
  border: "1px solid #e2e8f0",
  zIndex: 1000,
  overflow: "hidden",
  overscrollBehavior: "contain",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 12px",
  background: "#0f172a",
  color: "#fff",
};

const closeBtnStyle = {
  background: "transparent",
  border: "none",
  color: "#cbd5e1",
  fontSize: 20,
  cursor: "pointer",
  lineHeight: 1,
};

const controlsRowStyle = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  padding: "10px 12px",
  borderBottom: "1px solid #eef2f7",
};

const controlLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const selectStyle = {
  padding: "4px 8px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  background: "#fff",
  color: "#0f172a",
  cursor: "pointer",
};

const chipsRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "8px 12px",
  borderBottom: "1px solid #eef2f7",
};

const chipStyle = {
  padding: "3px 10px",
  borderRadius: 999,
  border: "1px solid #e2e8f0",
  fontSize: 11,
  fontWeight: 600,
  background: "#fff",
  color: "#475569",
  cursor: "pointer",
};

const chipDisabledStyle = {
  opacity: 0.4,
  cursor: "not-allowed",
};

const centerStyle = {
  height: 300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#64748b",
  fontSize: 13,
};
